import * as React from "react";

import { TimePrecision } from "./format";
import { DateInput, toDate } from "./formatters";
import { useDateTimeFormat } from "./useDateTimeFormat";

interface FormattedDateTimeProps {
  /** A `Date`, epoch-millisecond number, or date/ISO string. */
  value: DateInput;
  /** Time granularity to render. Defaults to `"minutes"`. */
  time?: TimePrecision;
  /** Append the short timezone name to the visible text. */
  timeZone?: boolean;
  /** Text rendered when `value` is missing or unparseable. Defaults to `""`. */
  fallback?: string;
  /**
   * When true (the default), the element carries a `title` tooltip showing the
   * full second-precision timestamp with timezone, even when the visible text
   * is coarser. Pass `false` to omit it.
   */
  showTooltip?: boolean;
  className?: string;
}

/**
 * Renders a date/time according to the user's active format preference,
 * re-rendering automatically when that preference changes. This is the
 * canonical way to display a wall-clock timestamp in component markup — prefer
 * it over inline `new Date(x).toLocaleString()`.
 */
export function FormattedDateTime({
  value,
  time = "minutes",
  timeZone = false,
  fallback = "",
  showTooltip = true,
  className,
}: FormattedDateTimeProps): React.ReactElement {
  const format = useDateTimeFormat();
  const date = toDate(value);

  if (date === null) {
    return <span className={className}>{fallback}</span>;
  }

  const text = format.format(date, { time, timeZone });
  const tooltip =
    showTooltip && time !== "none"
      ? format.format(date, { time: "seconds", timeZone: true })
      : undefined;

  return (
    <span className={className} title={tooltip}>
      {text}
    </span>
  );
}
